import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Errors, actions } from 'react-redux-form';
import PropTypes from 'prop-types';

import { getValidationMessages } from '../../../validators/validators';
import config from '../../../reducers/config';
import {
  getFetchFunction,
  ltLogger,
  reportFieldEngagement,
} from '../../../bootstrap';
import * as logtypes from '../../../common/logtypes';
import * as trackingevents from '../../../common/trackingeventnames';

const OTP_LENGTH = 6;

const stripToDigits = (val) =>
  (val || '').replace(/\D/g, '').slice(0, OTP_LENGTH);

const formatPhoneForDisplay = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10
    )}`;
  }
  return phone;
};

const VerifiedServiceOtpInput = ({
  step,
  dispatch,
  handleNext,
  handleSkipStep,
  formData,
  formMeta,
  fieldState,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const inputRef = useRef(null);
  const instructionText =
    step.otpInstructionText || 'Please enter the six digit code we sent';
  const attributeId = step.attributeid || 'VerificationCode';
  const resendCodeText = step.otpResendCodeText || 'Resend Code';
  const phoneNumber = formData?.PhoneNumber || '';
  const maskedPhone = formatPhoneForDisplay(phoneNumber);
  const model = `formData.${attributeId}`;
  const verifyOTPFieldEngagementValue = 'OTPCode';

  const rawValue = formData?.[attributeId] || '';
  const hasError =
    fieldState &&
    !fieldState.valid &&
    fieldState.touched &&
    !fieldState.pristine;

  const isResendDisabled = isResending || resendError !== null;

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (rawValue === '' && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [rawValue]);

  const updateRedux = (digits) => {
    dispatch(actions.change(model, digits));
    dispatch(actions.setTouched(model));
    dispatch(actions.resetValidity(model));
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const digits = stripToDigits(raw);
    if (raw !== digits) {
      e.target.value = digits;
    }
    updateRedux(digits);
    if (digits.length === OTP_LENGTH) {
      reportFieldEngagement(attributeId, verifyOTPFieldEngagementValue);
      setTimeout(() => handleNext?.(), 0);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = stripToDigits(e.clipboardData.getData('text'));
    if (!digits) return;
    if (inputRef.current) inputRef.current.value = digits;
    updateRedux(digits);
    if (digits.length === OTP_LENGTH) {
      reportFieldEngagement(attributeId, verifyOTPFieldEngagementValue);
      setTimeout(() => handleNext?.(), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const digits = stripToDigits(inputRef.current?.value);
      if (digits.length === OTP_LENGTH) {
        reportFieldEngagement(attributeId, verifyOTPFieldEngagementValue);
        handleNext?.();
      }
    }
  };

  const handleResendCode = async () => {
    if (isResending || resendError) return;

    ltLogger(logtypes.TRACK, trackingevents.OTP_RESEND_CLICKED, {
      ...(formMeta || {}),
      otpAction: 'resend_code',
    });

    setIsResending(true);

    if (inputRef.current) inputRef.current.value = '';
    dispatch(actions.change(model, ''));
    dispatch(actions.resetValidity(model));
    dispatch(actions.setTouched(model, false));
    dispatch(actions.setPristine(model, true));

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    const requestBody = {
      phoneNumber: phoneNumber,
      otpDeliveryMethod: 'SMS',
      FormGuid: formMeta?.formUuid || '',
    };

    const url = `${config.apiConfig.rootApiServerPath}/api/enrich/sendOtp`;

    try {
      const response = await getFetchFunction()(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      // Handle saveToState - update Redux state with any state changes
      if (data.saveToState) {
        Object.keys(data.saveToState).forEach((key) => {
          dispatch(actions.change(key, data.saveToState[key]));
        });
      }

      if (data.advance === true || data.maxAttemptsReached === true) {
        // Advance the form to the next step
        if (handleSkipStep) {
          handleSkipStep();
        }
        return;
      }

      // Handle errors that don't require advancing
      if (!response.ok || data.errorMessage) {
        setResendError(data.errorMessage || 'Failed to resend code.');
      }
    } catch {
      setResendError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyAnotherWay = () => {
    ltLogger(logtypes.TRACK, trackingevents.OTP_VERIFY_ANOTHER_WAY_CLICKED, {
      ...(formMeta || {}),
      otpAction: 'verify_another_way',
    });

    if (handleSkipStep) {
      handleSkipStep();
    }
  };

  return (
    <div className="verified-service-otp-container">
      <p className="verified-service-otp-instruction">
        {instructionText}
        {maskedPhone ? (
          <>
            <br className="verified-service-otp-mobile-break" /> to{' '}
            {maskedPhone}.
          </>
        ) : (
          '.'
        )}
      </p>

      <div className="verified-service-otp-input-group">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          maxLength={OTP_LENGTH}
          autoComplete="one-time-code"
          autoFocus
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          className={`verified-service-otp-input${
            hasError ? ' verified-service-otp-input--error' : ''
          }`}
          aria-invalid={!!hasError}
          aria-label="Verification code"
        />
      </div>

      <Errors
        model={model}
        show={(field) => field.touched && !field.valid}
        messages={getValidationMessages(step.validation)}
        component={(props) => (
          <p className="verified-service-otp-error-message">{props.children}</p>
        )}
      />

      {resendError && (
        <p className="verified-service-otp-error-message">{resendError}</p>
      )}

      <div className="verified-service-otp-branding">
        <img
          src="/form/assets/images/logos/verified-logo.svg"
          alt="Verified"
          className="verified-service-logo"
          height="16"
        />
      </div>

      <div className="verified-service-otp-actions">
        <button
          type="button"
          className={`verified-service-otp-link${
            resendError ? ' verified-service-otp-link--disabled' : ''
          }`}
          onClick={handleResendCode}
          disabled={isResendDisabled}
          style={{ position: 'relative', display: 'inline-block' }}
        >
          <span
            style={{
              opacity: isResending || resendError ? 0.5 : 1,
              color: isResending || resendError ? '#999' : undefined,
            }}
          >
            {resendCodeText}
          </span>
          {isResending && (
            <img
              src="/form/assets/images/misc/spinner.gif"
              alt=""
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '28px',
                height: '28px',
              }}
            />
          )}
        </button>

        <span className="verified-service-otp-separator">or</span>

        <button
          type="button"
          className="verified-service-otp-link"
          onClick={handleVerifyAnotherWay}
        >
          Verify Another Way
        </button>
      </div>
    </div>
  );
};

VerifiedServiceOtpInput.propTypes = {
  step: PropTypes.object.isRequired,
  handleNext: PropTypes.func.isRequired,
  handleSkipStep: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  formData: PropTypes.object,
  formMeta: PropTypes.object,
  fieldState: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
  const attrId = ownProps.step?.attributeid || 'VerificationCode';
  return {
    formData: state.formData,
    formMeta: state.formMeta,
    fieldState: state.forms?.formData?.[attrId],
  };
};

export default connect(mapStateToProps)(VerifiedServiceOtpInput);
